import { useEffect, useState } from 'react';
import './App.css';

import copy from './locales.json';
import guideCopy from './guide-locales.json';
import { languages, detectLanguage, languageStorageKey } from './languages';

const repoUrl = 'https://github.com/nicotech-studio/turbo-killer';
const releaseVersion = '1.3.0';
const releaseUrl = `${repoUrl}/releases/latest/download/TurboKiller.dmg`;
const releaseName = 'TurboKiller.dmg';

function App({ pagePath = window.location.pathname, initialLanguage } = {}) {
  const isGuide = /^\/guide\/?$/.test(pagePath);
  const [language, setLanguage] = useState(() => initialLanguage || detectLanguage());
  const t = copy[language];
  const guide = guideCopy[language];
  const homeHref = `/?lang=${language}`;
  const guideHref = `/guide/?lang=${language}`;

  useEffect(() => {
    try { localStorage.setItem(languageStorageKey, language); } catch { /* Storage may be disabled. */ }
    const url = new URL(window.location.href);
    url.searchParams.set('lang', language);
    window.history.replaceState(null, '', url);
    document.documentElement.lang = language;
    document.documentElement.dir = ['ar', 'he'].includes(language) ? 'rtl' : 'ltr';
    document.title = isGuide ? `${guide.title} | TurboKiller` : `TurboKiller — ${t.subtitle}`;
    const description = isGuide ? guide.description : t.description;
    const canonical = `https://turbokiller.nicotech.cc/${isGuide ? 'guide/' : ''}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonical);
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', document.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', description);
  }, [language, t, guide, isGuide]);

  return (
    <div className="site-shell" id="top">
      <div className="scanlines" aria-hidden="true" />

      <header className="nav frame">
        <a className="brand" href={isGuide ? homeHref : '#top'} dir="ltr">TurboKiller</a>
        <div className="nav-links">
          <a href={guideHref} aria-current={isGuide ? 'page' : undefined}>{guide.nav}</a>
          <a className="github-nav" href={repoUrl}><GitHubMark /><span>GITHUB</span><span aria-hidden="true">↗</span></a>
          <select aria-label={t.languageLabel} value={language} onChange={(e) => setLanguage(e.target.value)}>
            {languages.map((item) => <option key={item.value} value={item.value} lang={item.value} dir="auto">{item.label}</option>)}
          </select>
        </div>
      </header>

      <main className="frame">
        {isGuide ? <Guide guide={guide} t={t} language={language} homeHref={homeHref} /> : <>
        <section className="hero">
          <div className="hero-copy">
            <div className="prompt" dir="ltr">root@mac:~$ launch turbokiller</div>
            <h1 dir="ltr">TurboKiller<span className="cursor">_</span></h1>
            <h2>{t.subtitle}</h2>
            <p>{t.description}</p>

            <a className="repo-strip" href={repoUrl}>
              <GitHubMark />
              <span className="repo-state"><i />{t.repoPublic}</span>
              <span>{t.repoLicense}</span>
              <span className="repo-free">{t.repoFree}</span>
            </a>

            <div className="github-actions">
              <a className="release-card" href={releaseUrl}>
                <span className="release-icon" aria-hidden="true">↓</span>
                <span><small>{t.download}</small><strong dir="ltr">{releaseName}</strong></span>
                <span className="external" aria-hidden="true">↓</span>
              </a>
              <a className="repo-card" href={repoUrl}>
                <GitHubMark />
                <span><small>{t.repoAction}</small><strong dir="ltr">nicotech-studio / turbo-killer</strong></span>
                <span className="external" aria-hidden="true">↗</span>
              </a>
            </div>

            <div className="requirements" dir="ltr">{t.requirements}</div>
            <a className="release-notes" href={`${repoUrl}/blob/v${releaseVersion}/docs/releases/${releaseVersion}/${language}.md`}>{t.releaseNotes}</a>
          </div>

          <figure className="app-shot-shell">
            <div className="app-shot-bar"><span>{t.screenshotLabel}</span><span dir="ltr">v{releaseVersion}</span></div>
            <div className="app-shot-stage">
              <img className="app-shot" src={`/assets/screenshots/app-${language}.png`} alt={t.screenshotAlt} />
            </div>
          </figure>
        </section>

        <section className="facts terminal-section">
          {t.facts.map(([key, value]) => (
            <div className="fact-row" key={key}><span className="fact-key">{key}</span><span>{value}</span></div>
          ))}
        </section>

        <section className="monitoring terminal-section" aria-labelledby="monitoring-title">
          <h2 className="section-title" id="monitoring-title">{'// '}{t.monitoringTitle}</h2>
          <p className="monitoring-summary">{t.monitoringSummary}</p>
          <figure className="app-shot-shell monitoring-shot">
            <div className="app-shot-bar"><span>{t.monitoringTitle}</span><span dir="ltr">v{releaseVersion}</span></div>
            <a className="monitoring-image-link" href="/assets/screenshots/frequency-history-real.png" aria-describedby="monitoring-caption" target="_blank" rel="noreferrer">
              <img className="history-shot" src="/assets/screenshots/frequency-history-real.png" alt={t.monitoringTitle} width="1216" height="880" loading="lazy" />
            </a>
            <figcaption className="monitoring-caption" id="monitoring-caption">{t.monitoringCaption}</figcaption>
          </figure>
        </section>

        <section className="terminal-section" id="install">
          <div className="section-title">{'// '}{t.installTitle}</div>
          <div className="warning">! {t.installNote}</div>
          <ol className="install-list">
            {t.steps.map((step, index) => <li key={step}><span>0{index + 1}</span>{step}</li>)}
          </ol>
        </section>

        <section className="terminal-section usage">
          <div className="section-title">{'// '}{t.usageTitle}</div>
          <p className="usage-summary">{t.usageSummary}</p>
          <div className="compatibility">{t.compatibility}</div>
          <a className="usage-guide-link" href={guideHref}>{guide.readGuide}<span aria-hidden="true"> →</span></a>
        </section>

        <section className="terminal-section faq-section">
          <div className="section-title">{'// '}{t.faqTitle}</div>

          <div className="faq-list">
            {t.faq.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>
        </>}
      </main>

      <footer className="frame">
        <span>© 2026 NICOTECH STUDIO</span>
        <div className="footer-commands" dir="ltr">
          <a href={`${repoUrl}/releases/latest`} aria-label={t.releaseAria}><GitHubMark /><span>$ gh release view latest</span><b>↗</b></a>
          <a href={`${repoUrl}/issues`} aria-label={t.issueAria}><GitHubMark /><span>$ gh issue create</span><b>↗</b></a>
          <a href={`${repoUrl}/blob/main/LICENSE`} aria-label={t.licenseAria}><span className="cmd-icon">&gt;_</span><span>$ cat LICENSE</span><b>GPL-2.0</b></a>
        </div>
      </footer>
    </div>
  );
}


function Guide({ guide, t, language, homeHref }) {
  return (
    <article className="guide-document">
      <header className="guide-heading">
        <a className="guide-back" href={homeHref}>← {guide.home}</a>
        <h1>{guide.title}</h1>
        <p className="guide-requirements" dir="ltr">{t.requirements}</p>
      </header>
      {guide.sections.map(([heading, body], index) => (
        <section className="terminal-section guide-section" key={heading}>
          <h2>{heading}</h2>
          <p>{body}</p>
          {index === 2 && <a href={releaseUrl}>{t.download} — <bdi>TurboKiller.dmg</bdi> ↓</a>}
          {index === 3 && <figure className="app-shot-shell">
            <div className="app-shot-bar"><span>{t.screenshotLabel}</span><span dir="ltr">v{releaseVersion}</span></div>
            <div className="app-shot-stage"><img className="app-shot" src={`/assets/screenshots/app-${language}.png`} alt={t.screenshotAlt} width="680" height="328" /></div>
          </figure>}
          {index === 5 && <a href={`${repoUrl}/issues`}>{t.feedback} — GitHub Issues ↗</a>}
        </section>
      ))}
      <a className="guide-back" href={homeHref}>← {guide.home}</a>
    </article>
  );
}

function GitHubMark() {
  return (
    <svg className="github-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 .7a11.5 11.5 0 0 0-3.64 22.41c.58.1.79-.25.79-.56v-2.2c-3.22.7-3.9-1.37-3.9-1.37-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.71.08-.71 1.16.08 1.77 1.19 1.77 1.19 1.04 1.77 2.71 1.26 3.37.96.1-.75.41-1.26.74-1.55-2.57-.29-5.27-1.28-5.27-5.68 0-1.26.45-2.28 1.19-3.08-.12-.29-.52-1.47.11-3.05 0 0 .97-.31 3.16 1.18a10.95 10.95 0 0 1 5.76 0c2.19-1.49 3.15-1.18 3.15-1.18.63 1.58.23 2.76.12 3.05.74.8 1.18 1.82 1.18 3.08 0 4.41-2.71 5.38-5.29 5.67.42.36.79 1.06.79 2.14v3.18c0 .31.21.67.8.56A11.5 11.5 0 0 0 12 .7Z" />
    </svg>
  );
}

export default App;
